import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BulkUpload() {
  const [step, setStep] = useState(1);
  const [csvFile, setCsvFile] = useState(null);
  const [rawCsvData, setRawCsvData] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    document_id: '',
    document_name: '',
    folder_name: '',
    issue_date: '',
    validity_status: '',
    pdf_link: ''
  });
  const [qrPosition, setQrPosition] = useState({ x: 420, y: 700 });
  const [qrSize, setQrSize] = useState(80);
  const [qrPage, setQrPage] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const fileInputRef = useRef(null);

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    if (lines.length === 0) return { headers: [], rows: [] };

    const parseRow = (row) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseRow(lines[0]);
    const rows = lines.slice(1).map(line => {
      const values = parseRow(line);
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = values[index] || '';
      });
      return obj;
    });

    return { headers, rows };
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      setRawCsvData(text);
      
      const { headers, rows } = parseCSV(text);
      
      if (headers.length === 0) {
        toast.error('CSV file appears to be empty');
        return;
      }

      setColumns(headers);
      setParsedRows(rows);

      const autoMapping = { ...columnMapping };
      const expectedColumns = ['document_id', 'document_name', 'folder_name', 'issue_date', 'validity_status', 'pdf_link'];
      
      expectedColumns.forEach(expected => {
        const found = headers.find(h => 
          h.toLowerCase().replace(/[\s_-]/g, '') === expected.toLowerCase().replace(/[\s_-]/g, '')
        );
        if (found) {
          autoMapping[expected] = found;
        }
      });
      
      setColumnMapping(autoMapping);
      setStep(2);
      toast.success(`CSV loaded: ${rows.length} rows found`);
    };
    reader.onerror = () => {
      toast.error('Failed to read CSV file');
    };
    reader.readAsText(file);
  };

  const updatePreview = () => {
    if (parsedRows.length === 0) return;

    const preview = parsedRows.slice(0, 3).map(row => ({
      document_id: row[columnMapping.document_id] || 'N/A',
      document_name: row[columnMapping.document_name] || 'N/A',
      folder_name: row[columnMapping.folder_name] || 'N/A',
      issue_date: row[columnMapping.issue_date] || 'N/A',
      validity_status: row[columnMapping.validity_status] || 'Valid',
      pdf_link: row[columnMapping.pdf_link] || 'N/A'
    }));
    
    setPreviewData(preview);
  };

  const transformCsvData = () => {
    const headers = ['document_id', 'document_name', 'folder_name', 'issue_date', 'validity_status', 'pdf_link'];
    const headerLine = headers.join(',');
    
    const dataLines = parsedRows.map(row => {
      return headers.map(header => {
        const sourceColumn = columnMapping[header];
        let value = sourceColumn ? (row[sourceColumn] || '') : '';
        
        if (header === 'validity_status' && !value) {
          value = 'Valid';
        }
        
        if (value.includes(',') || value.includes('"')) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        
        return value;
      }).join(',');
    });

    return [headerLine, ...dataLines].join('\n');
  };

  const validateMapping = () => {
    const errors = [];
    if (!columnMapping.document_id) errors.push('Document ID required');
    if (!columnMapping.document_name) errors.push('Document Name required');
    if (!columnMapping.folder_name) errors.push('Folder Name required');
    if (!columnMapping.issue_date) errors.push('Issue Date required');
    if (!columnMapping.pdf_link) errors.push('PDF Link required');
    return errors;
  };

  const handleProcessBulk = async () => {
    const errors = validateMapping();
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      return;
    }

    setProcessing(true);

    try {
      const transformedCsv = transformCsvData();
      
      const formData = new FormData();
      formData.append('csv_data', transformedCsv);
      formData.append('qr_x', qrPosition.x.toString());
      formData.append('qr_y', qrPosition.y.toString());
      formData.append('qr_size', qrSize.toString());
      formData.append('qr_page', qrPage.toString());

      const response = await axios.post(`${API}/documents/bulk-upload`, formData);
      setResults(response.data);
      setStep(3);
      
      if (response.data.successful > 0) {
        toast.success(`Bulk upload complete! ${response.data.successful} documents processed`);
      } else {
        toast.error('No documents were processed successfully');
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error(error.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setProcessing(false);
    }
  };

  const downloadCSVTemplate = () => {
    const template = `document_id,document_name,folder_name,issue_date,validity_status,pdf_link
DOC-001,Sample Document 1,Sample Folder,2025-01-01,Valid,https://example.com/sample.pdf
DOC-002,Sample Document 2,Sample Folder,2025-01-15,Valid,https://example.com/sample2.pdf`;
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setStep(1);
    setCsvFile(null);
    setRawCsvData('');
    setParsedRows([]);
    setColumns([]);
    setColumnMapping({
      document_id: '',
      document_name: '',
      folder_name: '',
      issue_date: '',
      validity_status: '',
      pdf_link: ''
    });
    setResults(null);
    setPreviewData([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Step 1: Upload CSV */}
      {step === 1 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8">
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
            <div className="text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <FileSpreadsheet className="w-7 h-7 sm:w-8 sm:h-8 text-slate-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Bulk Upload via CSV</h2>
              <p className="text-sm sm:text-base text-slate-600">Upload multiple documents at once</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
              <div className="flex gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm text-amber-800">
                  <p className="font-medium mb-2">CSV Requirements:</p>
                  <ul className="list-disc list-inside space-y-0.5 sm:space-y-1">
                    <li><strong>Document ID</strong> - Unique ID</li>
                    <li><strong>Document Name</strong> - Display name</li>
                    <li><strong>Folder Name</strong> - Organization</li>
                    <li><strong>Issue Date</strong> - YYYY-MM-DD</li>
                    <li><strong>Validity Status</strong> - Valid/Revoked/Invalid</li>
                    <li><strong>PDF Link</strong> - URL to PDF</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button 
              onClick={downloadCSVTemplate} 
              variant="outline" 
              className="w-full h-10 sm:h-11"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>

            <div className="space-y-3 sm:space-y-4">
              <Label htmlFor="csv-upload" className="text-slate-700 font-medium">Upload CSV File</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 sm:p-8 text-center hover:border-slate-400 transition-colors">
                <input
                  ref={fileInputRef}
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="hidden"
                  data-testid="csv-file-input"
                />
                <Upload className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-sm text-slate-600 mb-2">
                  {csvFile ? csvFile.name : 'Drop CSV here or click to browse'}
                </p>
                <Button 
                  onClick={() => fileInputRef.current?.click()} 
                  variant="outline"
                  size="sm"
                  data-testid="browse-csv-btn"
                >
                  Browse Files
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Column Mapping & QR Position */}
      {step === 2 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8">
          <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
            <div className="text-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Configure Bulk Upload</h2>
              <p className="text-sm sm:text-base text-slate-600 mt-1">{parsedRows.length} rows detected in CSV</p>
            </div>
            
            {/* Column Mapping */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm sm:text-base">
                <span className="w-5 h-5 sm:w-6 sm:h-6 bg-slate-900 text-white rounded-full text-xs sm:text-sm flex items-center justify-center">1</span>
                Map CSV Columns
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-xs sm:text-sm">Document ID *</Label>
                  <select
                    value={columnMapping.document_id}
                    onChange={(e) => {
                      setColumnMapping({...columnMapping, document_id: e.target.value});
                      setTimeout(updatePreview, 100);
                    }}
                    className="w-full h-9 sm:h-10 border border-slate-200 rounded-md px-3 mt-1 text-sm"
                    data-testid="map-document-id"
                  >
                    <option value="">Select column...</option>
                    {columns.map(col => <option key={col} value={col}>{col}</option>)}
                  </select>
                </div>

                <div>
                  <Label className="text-xs sm:text-sm">Document Name *</Label>
                  <select
                    value={columnMapping.document_name}
                    onChange={(e) => {
                      setColumnMapping({...columnMapping, document_name: e.target.value});
                      setTimeout(updatePreview, 100);
                    }}
                    className="w-full h-9 sm:h-10 border border-slate-200 rounded-md px-3 mt-1 text-sm"
                    data-testid="map-document-name"
                  >
                    <option value="">Select column...</option>
                    {columns.map(col => <option key={col} value={col}>{col}</option>)}
                  </select>
                </div>

                <div>
                  <Label className="text-xs sm:text-sm">Folder Name *</Label>
                  <select
                    value={columnMapping.folder_name}
                    onChange={(e) => {
                      setColumnMapping({...columnMapping, folder_name: e.target.value});
                      setTimeout(updatePreview, 100);
                    }}
                    className="w-full h-9 sm:h-10 border border-slate-200 rounded-md px-3 mt-1 text-sm"
                    data-testid="map-folder-name"
                  >
                    <option value="">Select column...</option>
                    {columns.map(col => <option key={col} value={col}>{col}</option>)}
                  </select>
                </div>

                <div>
                  <Label className="text-xs sm:text-sm">Issue Date *</Label>
                  <select
                    value={columnMapping.issue_date}
                    onChange={(e) => {
                      setColumnMapping({...columnMapping, issue_date: e.target.value});
                      setTimeout(updatePreview, 100);
                    }}
                    className="w-full h-9 sm:h-10 border border-slate-200 rounded-md px-3 mt-1 text-sm"
                    data-testid="map-issue-date"
                  >
                    <option value="">Select column...</option>
                    {columns.map(col => <option key={col} value={col}>{col}</option>)}
                  </select>
                </div>

                <div>
                  <Label className="text-xs sm:text-sm">Validity Status</Label>
                  <select
                    value={columnMapping.validity_status}
                    onChange={(e) => {
                      setColumnMapping({...columnMapping, validity_status: e.target.value});
                      setTimeout(updatePreview, 100);
                    }}
                    className="w-full h-9 sm:h-10 border border-slate-200 rounded-md px-3 mt-1 text-sm"
                    data-testid="map-validity-status"
                  >
                    <option value="">Default to "Valid"</option>
                    {columns.map(col => <option key={col} value={col}>{col}</option>)}
                  </select>
                </div>

                <div>
                  <Label className="text-xs sm:text-sm">PDF Link *</Label>
                  <select
                    value={columnMapping.pdf_link}
                    onChange={(e) => {
                      setColumnMapping({...columnMapping, pdf_link: e.target.value});
                      setTimeout(updatePreview, 100);
                    }}
                    className="w-full h-9 sm:h-10 border border-slate-200 rounded-md px-3 mt-1 text-sm"
                    data-testid="map-pdf-link"
                  >
                    <option value="">Select column...</option>
                    {columns.map(col => <option key={col} value={col}>{col}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Data Preview */}
            {previewData.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3 sm:p-4 border border-slate-200 overflow-x-auto">
                <h4 className="font-medium text-slate-900 mb-3 text-sm">Preview (First 3 rows)</h4>
                <table className="w-full text-xs sm:text-sm min-w-[500px]">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-2 text-slate-600">Doc ID</th>
                      <th className="text-left py-2 px-2 text-slate-600">Name</th>
                      <th className="text-left py-2 px-2 text-slate-600">Folder</th>
                      <th className="text-left py-2 px-2 text-slate-600">Date</th>
                      <th className="text-left py-2 px-2 text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-2 px-2 font-mono">{row.document_id}</td>
                        <td className="py-2 px-2">{row.document_name}</td>
                        <td className="py-2 px-2">{row.folder_name}</td>
                        <td className="py-2 px-2">{row.issue_date}</td>
                        <td className="py-2 px-2">{row.validity_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* QR Position Settings */}
            <div className="border-t pt-4 sm:pt-6 mt-4 sm:mt-6">
              <h3 className="font-semibold text-slate-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                <span className="w-5 h-5 sm:w-6 sm:h-6 bg-slate-900 text-white rounded-full text-xs sm:text-sm flex items-center justify-center">2</span>
                QR Code Position Template
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 mb-3 sm:mb-4">
                This position will be applied to all documents in the batch.
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <Label className="text-xs sm:text-sm">X Position</Label>
                  <Input
                    type="number"
                    value={qrPosition.x}
                    onChange={(e) => setQrPosition({ ...qrPosition, x: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="h-9 sm:h-10 mt-1"
                    data-testid="qr-x-position"
                  />
                </div>
                <div>
                  <Label className="text-xs sm:text-sm">Y Position</Label>
                  <Input
                    type="number"
                    value={qrPosition.y}
                    onChange={(e) => setQrPosition({ ...qrPosition, y: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="h-9 sm:h-10 mt-1"
                    data-testid="qr-y-position"
                  />
                </div>
                <div>
                  <Label className="text-xs sm:text-sm">QR Size</Label>
                  <Input
                    type="number"
                    value={qrSize}
                    onChange={(e) => setQrSize(parseInt(e.target.value) || 80)}
                    min="5"
                    max="200"
                    className="h-9 sm:h-10 mt-1"
                    data-testid="qr-size"
                  />
                </div>
                <div>
                  <Label className="text-xs sm:text-sm">Page #</Label>
                  <Input
                    type="number"
                    value={qrPage}
                    onChange={(e) => setQrPage(parseInt(e.target.value) || 1)}
                    min="1"
                    className="h-9 sm:h-10 mt-1"
                    data-testid="qr-page"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
              <Button 
                onClick={() => setStep(1)} 
                variant="outline" 
                className="flex-1 h-10 sm:h-11 order-2 sm:order-1"
                data-testid="back-btn"
              >
                Back
              </Button>
              <Button 
                onClick={handleProcessBulk} 
                disabled={processing}
                className="flex-1 h-10 sm:h-11 bg-slate-900 hover:bg-slate-800 text-white order-1 sm:order-2"
                data-testid="process-bulk-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Process {parsedRows.length} Documents
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && results && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8">
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
            <div className="text-center">
              <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 ${
                results.failed === 0 ? 'bg-green-100' : 'bg-amber-100'
              }`}>
                {results.failed === 0 ? (
                  <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" />
                ) : (
                  <AlertCircle className="w-7 h-7 sm:w-8 sm:h-8 text-amber-600" />
                )}
              </div>

              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                {results.failed === 0 ? 'Bulk Upload Complete!' : 'Upload Finished with Errors'}
              </h2>
              
              <div className="flex justify-center gap-6 sm:gap-8 text-slate-600">
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-green-600">{results.successful}</p>
                  <p className="text-xs sm:text-sm">Successful</p>
                </div>
                {results.failed > 0 && (
                  <div className="text-center">
                    <p className="text-2xl sm:text-3xl font-bold text-red-600">{results.failed}</p>
                    <p className="text-xs sm:text-sm">Failed</p>
                  </div>
                )}
              </div>
            </div>

            {results.results && results.results.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                <p className="font-medium text-green-900 mb-2 text-sm">Successfully Processed:</p>
                <div className="space-y-1 text-xs sm:text-sm text-green-800 max-h-32 sm:max-h-40 overflow-y-auto">
                  {results.results.map((result, i) => (
                    <p key={i}>Row {result.row}: {result.document_id}</p>
                  ))}
                </div>
              </div>
            )}

            {results.errors && results.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <p className="font-medium text-red-900 mb-2 text-sm">Errors:</p>
                <div className="space-y-2 text-xs sm:text-sm text-red-800 max-h-32 sm:max-h-40 overflow-y-auto">
                  {results.errors.map((error, i) => (
                    <div key={i} className="pb-2 border-b border-red-100 last:border-0">
                      <p className="font-medium">Row {error.row}:</p>
                      <p className="break-words">{error.error}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button 
              onClick={resetForm} 
              className="w-full h-10 sm:h-11 bg-slate-900 hover:bg-slate-800"
              data-testid="upload-another-btn"
            >
              Upload Another Batch
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
